/**
 * Vista móvil de envíos con cards paginadas
 */
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Stack,
    Alert,
    CircularProgress,
    Pagination,
    Card,
    Chip,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import CommentIcon from '@mui/icons-material/Comment';
import RouteIcon from '@mui/icons-material/Route';
import WarningIcon from '@mui/icons-material/Warning';
import { esClienteFinal, estaEstadoBloqueado, generarUrlTracking } from '../../helpers';
import { FONDO_CLIENTE_FINAL } from '../../constants';
import ChipClienteFinal from '../ui/ChipClienteFinal';
import Utils from '../../../../utils/Utils';

// Mapa de colores de estados (paleta semántica profesional)
const COLORES_ESTADO = {
    '-1': '#D1D5DB',  // Gris claro - Estado bloqueado
    '0': '#D1D5DB',   // Gris claro - Estado bloqueado
    '1': '#3B82F6',   // Azul - Expedición registrada
    '2': '#9CA3AF',   // Gris - Sin estado
    '3': '#F59E0B',   // Ámbar - Pendiente salida a reparto
    '4': '#06B6D4',   // Cyan - En reparto
    '5': '#22C55E',   // Verde - Entregado
    '6': '#B45309',   // Naranja oscuro - Retenido en almacén
    '7': '#8B5CF6',   // Púrpura - En tránsito entre islas
    '9': '#EF4444',   // Rojo - Cancelado
};

export default function EnviosMobileList({
    rows,
    loading,
    onOpenComentario,
    onOpenConfirmEstado,
    ejercicio,
}) {
    const [page, setPage] = useState(1);
    const pageSize = 10;

    useEffect(() => {
        setPage(1);
    }, [rows]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: '#cc071e' }} />
            </Box>
        );
    }

    if (!rows?.length) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                No hay envíos para mostrar
            </Alert>
        );
    }

    const totalPages = Math.ceil(rows.length / pageSize);
    const start = (page - 1) * pageSize;
    const pageRows = rows.slice(start, start + pageSize);

    return (
        <Box sx={{ width: '100%' }}>
            <Stack spacing={1.5}>
                {pageRows.map((envio) => {
                    const estado = envio.tracking_estado;
                    const colorEstado = COLORES_ESTADO[String(estado)] || '#6c757d';
                    const estadoBloqueado = estaEstadoBloqueado(estado);
                    const tieneComentario =
                        String(envio?.tracking_comentario ?? '').trim() !== '';
                    const url = generarUrlTracking(envio, ejercicio);

                    return (
                        <Card
                            key={`${envio.serie}-${envio.albaran}-${envio.linea ?? ''}`}
                            sx={{
                                borderLeft: `4px solid ${colorEstado}`,
                                ...(esClienteFinal(envio) && { backgroundColor: FONDO_CLIENTE_FINAL }),
                                boxShadow:
                                    '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
                                transition: 'box-shadow 0.2s',
                                '&:hover': {
                                    boxShadow:
                                        '0 4px 6px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)',
                                },
                            }}
                        >
                            <Box sx={{ p: 1.5 }}>
                                {/* Header: Albarán + Tiene comentario */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 1,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 14,
                                            color: '#cc071e',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        {envio.serie}-{envio.albaran}
                                    </Typography>
                                    {tieneComentario && (
                                        <WarningIcon
                                            fontSize="small"
                                            sx={{ color: 'warning.main', opacity: 0.8 }}
                                            titleAccess="Tiene comentario"
                                        />
                                    )}
                                </Box>

                                {/* Nombre del cliente */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 15,
                                            fontWeight: 600,
                                            color: '#343a40',
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {envio.nombre}
                                    </Typography>
                                    {esClienteFinal(envio) && <ChipClienteFinal />}
                                </Box>

                                {/* Información condensada con iconos */}
                                <Stack spacing={0.5}>
                                    {/* Bultos + Agencia */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 1,
                                            fontSize: 12,
                                            color: '#6c757d',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                            <InventoryIcon sx={{ fontSize: 14 }} />
                                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                                                {envio.bultos || 0} bultos
                                            </Typography>
                                        </Box>
                                        {envio.agencia && (
                                            <>
                                                <Typography sx={{ fontSize: 12 }}>·</Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.3,
                                                    }}
                                                >
                                                    <BusinessIcon sx={{ fontSize: 14 }} />
                                                    <Typography sx={{ fontSize: 12 }}>
                                                        {envio.agencia}
                                                    </Typography>
                                                </Box>
                                            </>
                                        )}
                                    </Box>

                                    {/* Nº ruta + Ruta */}
                                    {(envio.counterDelivery || envio.nameDelivery) && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: 1,
                                                fontSize: 12,
                                                color: '#6c757d',
                                            }}
                                        >
                                            {envio.counterDelivery && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.3,
                                                    }}
                                                >
                                                    <RouteIcon sx={{ fontSize: 14 }} />
                                                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                                                        Ruta {envio.counterDelivery}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {envio.nameDelivery && (
                                                <>
                                                    <Typography sx={{ fontSize: 12 }}>·</Typography>
                                                    <Typography sx={{ fontSize: 12 }}>
                                                        {envio.nameDelivery}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                    )}

                                    {/* Isla + Fecha ruta */}
                                    {(envio.tracking_destino || envio.ruta_fecha) && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: 1,
                                                fontSize: 12,
                                                color: '#6c757d',
                                            }}
                                        >
                                            {envio.tracking_destino && (
                                                <Typography sx={{ fontSize: 12 }}>
                                                    {envio.tracking_destino}
                                                </Typography>
                                            )}
                                            {envio.ruta_fecha && (
                                                <>
                                                    <Typography sx={{ fontSize: 12 }}>·</Typography>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.3,
                                                        }}
                                                    >
                                                        <CalendarTodayIcon sx={{ fontSize: 13 }} />
                                                        <Typography sx={{ fontSize: 12 }}>
                                                            {envio.ruta_fecha}
                                                        </Typography>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                    )}

                                    {/* Estado con link si aplica */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            fontSize: 12,
                                        }}
                                    >
                                        <LocalShippingIcon
                                            sx={{ fontSize: 14, color: colorEstado }}
                                        />
                                        {url ? (
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    textDecoration: 'none',
                                                    color: colorEstado,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        textDecoration: 'underline',
                                                        '&:hover': {
                                                            opacity: 0.8,
                                                        },
                                                    }}
                                                >
                                                    {Utils.toCapitalize(
                                                        envio.tracking_descripcion || 'Sin estado'
                                                    )}
                                                </Typography>
                                            </a>
                                        ) : (
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    color: colorEstado,
                                                }}
                                            >
                                                {Utils.toCapitalize(
                                                    envio.tracking_descripcion || 'Sin estado'
                                                )}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Expedición si existe */}
                                    {envio.expedicionAsigna && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                fontSize: 12,
                                                color: '#495057',
                                            }}
                                        >
                                            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                                                Exp: {envio.expedicionAsigna}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>

                                {/* Botones de acción */}
                                {(!estadoBloqueado || tieneComentario) && (
                                    <Stack
                                        direction="row"
                                        spacing={0.75}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            mt: 1.5,
                                            flexWrap: 'wrap',
                                            gap: 0.75,
                                        }}
                                    >
                                        {!estadoBloqueado && (
                                            <Button
                                                size="small"
                                                startIcon={
                                                    <PublishedWithChangesIcon sx={{ fontSize: 16 }} />
                                                }
                                                variant="outlined"
                                                sx={{
                                                    fontSize: 12,
                                                    px: 1.25,
                                                    py: 0.5,
                                                    color: '#198754',
                                                    borderColor: '#198754',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        color: '#157347',
                                                        borderColor: '#157347',
                                                        backgroundColor: 'rgba(25, 135, 84, 0.04)',
                                                    },
                                                    '& .MuiButton-startIcon': { mr: 0.5 },
                                                }}
                                                onClick={() => onOpenConfirmEstado(envio)}
                                            >
                                                Marcar entregado
                                            </Button>
                                        )}

                                        {(tieneComentario || !estadoBloqueado) && (
                                            <Button
                                                size="small"
                                                startIcon={<CommentIcon sx={{ fontSize: 16 }} />}
                                                variant="outlined"
                                                sx={{
                                                    fontSize: 12,
                                                    px: 1.25,
                                                    py: 0.5,
                                                    color: '#495057',
                                                    borderColor: '#495057',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        color: '#212529',
                                                        borderColor: '#212529',
                                                        backgroundColor: 'rgba(0,0,0,0.04)',
                                                    },
                                                    '& .MuiButton-startIcon': { mr: 0.5 },
                                                }}
                                                onClick={() => onOpenComentario(envio)}
                                            >
                                                {tieneComentario ? 'Ver comentario' : 'Comentario'}
                                            </Button>
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        </Card>
                    );
                })}
            </Stack>

            {/* Paginación */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        size="small"
                    />
                </Box>
            )}
        </Box>
    );
}
